/**
 * 验证码发送流程公共 Hook：发送锁 + 重发冷却倒计时（默认 60s）+ 卸载保护。
 * 错误呈现（toast / 内联 error）留在调用方：execute 抛错时不开启冷却并向上冒泡。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseSmsCodeFlowOptions {
  /** 发送成功后的重发冷却秒数，默认 60 */
  cooldownSeconds?: number;
}

export function useSmsCodeFlow(options?: UseSmsCodeFlowOptions) {
  const cooldownSeconds = options?.cooldownSeconds ?? 60;
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // 重发冷却倒计时（统一引擎，卸载自动清理）
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  /**
   * 发送验证码：execute 抛错视为失败（不开冷却，错误冒泡给调用方呈现）。
   * 内部含发送锁与冷却检查，调用方只需保留业务前置校验。
   */
  const send = useCallback(
    async (execute: () => Promise<unknown>): Promise<void> => {
      if (sending || countdown > 0) return;
      setSending(true);
      try {
        await execute();
        if (mountedRef.current) setCountdown(cooldownSeconds);
      } finally {
        if (mountedRef.current) setSending(false);
      }
    },
    [sending, countdown, cooldownSeconds],
  );

  return { countdown, sending, send, setCountdown };
}